"use client"

import { useEffect, useRef, useState } from "react"
import { Bell, X } from "lucide-react"
import { FreeReminderForm } from "@/components/FreeReminderForm"

type Props = {
  openCallId: string
  /** Where this trigger lives — used for analytics on conversion. */
  source?: "open_call_card" | "feed"
  /** Card-image overlay button style (white, drop-shadow) vs. default. */
  overlay?: boolean
}

/**
 * Card-level entry point for the free deadline reminder. Renders a small bell
 * trigger; clicking opens a popover with the email form. Closes on outside
 * click or Esc. Stops event propagation so it doesn't activate the card link.
 */
export function CardReminderTrigger({ openCallId, source = "open_call_card", overlay = false }: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        aria-label="Remind me before this closes"
        title="Remind me before this closes"
        className={
          overlay
            ? "p-1.5 bg-transparent border-0 shadow-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] [&_svg]:text-white hover:opacity-80 transition-opacity"
            : "p-1.5 text-[var(--muted-foreground)] hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
        }
      >
        <Bell size={15} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-9 z-50 w-72 bg-white border border-[var(--border)] rounded-xl shadow-lg p-3.5 animate-in fade-in slide-in-from-top-1 duration-150"
          role="dialog"
          aria-label="Set a free deadline reminder"
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-1.5 text-[13px] font-semibold text-[var(--foreground)]">
                <Bell size={13} className="text-amber-600" />
                Remind me before this closes
              </div>
              <p className="text-[11px] text-[var(--muted-foreground)] mt-0.5">
                One free reminder · no account needed
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="p-0.5 -mr-1 -mt-1 rounded hover:bg-[var(--secondary)] transition-colors text-[var(--muted-foreground)]"
            >
              <X size={14} />
            </button>
          </div>
          <FreeReminderForm openCallId={openCallId} source={source} compact />
          <p className="text-[11px] text-[var(--muted-foreground)] mt-2">
            For alerts on every deadline + saved lists,{" "}
            <a href="/pro" className="underline hover:text-[var(--foreground)]">go Saver</a>.
          </p>
        </div>
      )}
    </div>
  )
}
